import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { EmptyState } from '../../EmptyState';
import { Pagination } from '../../Pagination';
import { Table, TableBody, TableCell, TableRow } from '../../ui/Table';
import { GridFilterBar } from '../shared/GridFilterBar';
import { GridHeader } from '../shared/GridHeader';
import { useGridUrlState } from '../shared/useGridUrlState';
import type { PaginationInfo } from '../shared/types';
import type { DataGridConfig } from './types';

const TEST_ID_PREFIX = 'datagrid';

type DataGridProps<TRow> = {
  config: DataGridConfig<TRow>;
  rows: TRow[];
  pagination?: PaginationInfo | null;
  headerActions?: React.ReactNode;
};

export function DataGrid<TRow>({ config, rows, pagination, headerActions }: DataGridProps<TRow>) {
  const { sort, filters, search, setSort, setFilter, setSearch, setPage } = useGridUrlState(config);
  const navigate = useNavigate();
  const showPagination = config.pagination !== false && pagination;
  const testId = config.rowTestId ?? `${TEST_ID_PREFIX}-row`;

  const linkProps = (href: string | undefined) =>
    href
      ? {
          role: 'link' as const,
          tabIndex: 0,
          onClick: () => navigate(href),
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(href);
            }
          },
          className:
            'cursor-pointer transition-colors hover:bg-foreground/[0.06] focus-visible:bg-foreground/[0.06] focus-visible:outline-none',
        }
      : {};

  return (
    <div className="flex flex-col gap-4">
      <GridFilterBar
        columns={config.columns}
        standaloneFilters={config.filters}
        filters={filters}
        onFilterChange={setFilter}
        search={
          config.search
            ? { placeholder: config.search.placeholder, value: search, onChange: setSearch }
            : undefined
        }
        headerActions={headerActions}
        testIdPrefix={TEST_ID_PREFIX}
      />

      {rows.length === 0 ? (
        <div className="rounded-md border border-border">
          <EmptyState
            title={config.emptyState?.title ?? 'No results.'}
            description={config.emptyState?.description}
          />
        </div>
      ) : config.card ? (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {rows.map((row) => {
            const { className, ...props } = linkProps(config.rowLink?.(row));
            return (
              <li
                key={config.getRowId(row)}
                data-testid={testId}
                {...props}
                className={cn('flex flex-col rounded-md border border-border p-4', className)}
              >
                {config.card?.(row)}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <Table className="[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-card">
            <GridHeader
              columns={config.columns}
              sort={sort}
              onSortChange={setSort}
              testIdPrefix={TEST_ID_PREFIX}
            />
            <TableBody>
              {rows.map((row) => {
                const id = config.getRowId(row);
                const rowProps = linkProps(config.rowLink?.(row));
                return (
                  <TableRow key={id} data-testid={testId} {...rowProps}>
                    {config.columns.map((col) => {
                      const content = col.cell
                        ? col.cell(row)
                        : (col.accessor(row) as React.ReactNode);
                      return (
                        <TableCell
                          key={col.id}
                          className={cn(
                            col.align === 'right' && 'text-right',
                            col.align === 'center' && 'text-center',
                          )}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {showPagination && (
        <Pagination
          page={pagination.page}
          perPage={pagination.perPage}
          total={pagination.total}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
